// ============================================
// Sổ bằng chứng bán hàng 18+
// ============================================
// Màn để TRÌNH KHI BỊ THANH TRA. Vì vậy hai thứ được ưu tiên: lọc theo khoảng
// ngày, và tải CSV. Không có biểu đồ, không có insight — thanh tra cần bản ghi,
// không cần phân tích.
//
// ⚠️ Mỗi dòng là MỘT ĐƠN HÀNG, không phải một sản phẩm. Các món 18+ trong đơn
// nằm gộp ở chuỗi `restricted_items`.
//
// ⚠️ Sổ KHÔNG chứa thông tin cá nhân khách, chỉ có `customer_ref`. Đừng thêm cột
// tên hay liên hệ vào đây kể cả khi tra ngược được: một ảnh chụp màn hình đưa
// cho thanh tra không được mang theo danh tính khách.

import { useMemo, useState } from 'react';
import {
  isYes,
  RESTRICTED_SALES_DEFAULT_DAYS,
  RESTRICTED_SALES_MAX_RANGE_DAYS,
  type RestrictedSalesParams,
} from '@conai/shared-types';
import { useI18n } from '../../i18n';
import { PageContainer, PageHeader, Pagination } from '../../components/shared';
import { useDebounce } from '../../hooks/useDebounce';
import {
  restrictedSalesRangeError,
  useExportRestrictedSales,
  useRestrictedSales,
} from '../../hooks/useRestrictedSales';

const PAGE_SIZE = 20;

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RestrictedSalesPage() {
  const { t } = useI18n();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [orderCodeInput, setOrderCodeInput] = useState('');
  // Lọc theo cờ VI PHẠM (`sold_alcohol_during_curfew`), không phải cờ trung
  // tính `curfew_active_at_sale` — xem chú thích ở type.
  const [violationsOnly, setViolationsOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const debouncedOrderCode = useDebounce(orderCodeInput, 500);

  const params = useMemo<RestrictedSalesParams>(
    () => ({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      order_code: debouncedOrderCode || undefined,
      // Chỉ gửi khi bật. Gửi `false` là yêu cầu backend lọc ra những đơn KHÔNG
      // vi phạm — khác hẳn ý "đừng lọc theo tiêu chí này".
      sold_alcohol_during_curfew: violationsOnly ? true : undefined,
      page: currentPage,
      limit: PAGE_SIZE,
    }),
    [startDate, endDate, debouncedOrderCode, violationsOnly, currentPage]
  );

  const rangeErrorKey = restrictedSalesRangeError(params);
  const listQuery = useRestrictedSales(params);
  const { exportCsv, isExporting } = useExportRestrictedSales();

  const rows = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalItems = meta?.count ?? rows.length;
  // Ưu tiên `total_pages` của backend. `next`/`previous` LUÔN null ở endpoint
  // này (BE phân trang tay) nên không dùng để biết còn trang hay không.
  const totalPages = Math.max(1, meta?.total_pages ?? Math.ceil(totalItems / PAGE_SIZE));

  const resetPageAnd = (apply: () => void) => {
    apply();
    setCurrentPage(1);
  };

  return (
    <PageContainer>
      <PageHeader title={t('restrictedSales')} subtitle={t('restrictedSalesSubtitle')} />

      {/* Bộ lọc */}
      <div className="mb-4 rounded-xl bg-white p-4" style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">{t('rsStartDate')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => resetPageAnd(() => setStartDate(e.target.value))}
              className="h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">{t('rsEndDate')}</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => resetPageAnd(() => setEndDate(e.target.value))}
              className="h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">{t('rsOrderCode')}</label>
            <input
              value={orderCodeInput}
              onChange={(e) => resetPageAnd(() => setOrderCodeInput(e.target.value))}
              placeholder={t('rsOrderCodePlaceholder')}
              className="h-10 w-48 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400"
            />
          </div>
          <label className="flex h-10 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={violationsOnly}
              onChange={(e) => resetPageAnd(() => setViolationsOnly(e.target.checked))}
              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-200"
            />
            <span className="text-sm text-slate-700">{t('rsViolationsOnly')}</span>
          </label>

          <button
            onClick={() => exportCsv(params)}
            disabled={isExporting || rangeErrorKey !== null}
            className="ml-auto h-10 rounded-lg bg-[#0288D1] px-4 text-sm font-semibold text-white hover:bg-[#0277BD] disabled:opacity-60"
          >
            {isExporting ? t('rsExporting') : t('rsDownloadCsv')}
          </button>
        </div>

        {rangeErrorKey ? (
          <p className="mt-3 text-xs text-red-600">
            {t(rangeErrorKey, { max: RESTRICTED_SALES_MAX_RANGE_DAYS })}
          </p>
        ) : !startDate && !endDate ? (
          // Không chọn ngày thì backend tự lấy 90 ngày gần nhất — nói ra để
          // không ai tưởng đang xem toàn bộ lịch sử.
          <p className="mt-3 text-xs text-slate-400">
            {t('rsDefaultRangeHint', { days: RESTRICTED_SALES_DEFAULT_DAYS })}
          </p>
        ) : null}
      </div>

      {/* Bảng */}
      <div className="overflow-hidden rounded-xl bg-white" style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}>
        {rangeErrorKey ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">{t('rsFixRangeToSearch')}</div>
        ) : listQuery.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">{t('loading')}</div>
        ) : listQuery.isError ? (
          <div className="px-6 py-12 text-center">
            <p className="mb-3 text-sm text-slate-500">{t('rsLoadError')}</p>
            <button
              onClick={() => listQuery.refetch()}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t('avRetry')}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-slate-500">{t('rsEmpty')}</p>
            {/* STORE_ADMIN chỉ thấy cửa hàng mình được gán. Danh sách rỗng rất
                dễ là "tài khoản chưa được gán cửa hàng" chứ không phải "không
                có giao dịch nào" — hai chuyện này dẫn tới hai hành động hoàn
                toàn khác nhau. */}
            <p className="mt-2 text-xs text-slate-400">{t('rsEmptyScopeHint')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs uppercase text-slate-500">
                  <th className="px-4 py-3 font-medium">{t('rsOccurredAt')}</th>
                  <th className="px-4 py-3 font-medium">{t('rsOrderCode')}</th>
                  <th className="px-4 py-3 font-medium">{t('rsStore')}</th>
                  <th className="px-4 py-3 font-medium">{t('rsRestrictedItems')}</th>
                  <th className="px-4 py-3 font-medium">{t('rsVerification')}</th>
                  <th className="px-4 py-3 font-medium">{t('rsCurfew')}</th>
                  <th className="px-4 py-3 font-medium">{t('rsCustomerRef')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  // Chỉ cờ này mới là bán sai. `curfew_active_at_sale` là trung
                  // tính: giờ cấm chỉ cấm rượu, nên một đơn chỉ có thuốc lá bán
                  // lúc 23:00 vẫn bật cờ đó mà không vi phạm gì.
                  const isViolation = isYes(row.sold_alcohol_during_curfew);

                  return (
                    <tr
                      // Không có field `id` — `order_code` là khoá tự nhiên.
                      key={row.order_code || `row-${index}`}
                      className={`border-b border-slate-50 last:border-0 ${
                        isViolation ? 'bg-red-50/60' : 'hover:bg-slate-50/60'
                      }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                        {formatDateTime(row.occurred_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-900">
                        {row.order_code}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{row.store_name || '—'}</div>
                        {row.market ? <div className="text-xs text-slate-400">{row.market}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {/* Chuỗi BE gộp sẵn để đọc. Đừng tách ra parse. */}
                        <div className="max-w-xs">{row.restricted_items || '—'}</div>
                        {row.highest_min_age != null ? (
                          <div className="mt-0.5 text-xs text-slate-400">
                            {t('rsHighestMinAge', { age: row.highest_min_age })}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>
                          {row.verification_method ?? '—'}
                          {row.verified_age != null ? ` · ${row.verified_age}` : ''}
                        </div>
                        {/* Người duyệt hay máy duyệt là hai câu trả lời khác
                            nhau trước thanh tra. `reviewed_by` để trống với hồ
                            sơ máy duyệt (khoá ngoại sang user), nên phải đọc
                            `decided_by` chứ không suy từ chỗ trống đó. */}
                        {row.decided_by === 'MRZ' ? (
                          <div className="mt-0.5 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                            {t('rsDecidedByMrz')}
                          </div>
                        ) : row.reviewed_by ? (
                          <div className="text-xs text-slate-400">{row.reviewed_by}</div>
                        ) : (
                          <div className="text-xs text-slate-400">—</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isViolation ? (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                            {t('rsSoldAlcoholDuringCurfew')}
                          </span>
                        ) : isYes(row.curfew_active_at_sale) ? (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                            {t('rsDuringCurfew')}
                          </span>
                        ) : isYes(row.curfew_configured) ? (
                          <span className="text-xs text-slate-400">{t('rsOutsideCurfew')}</span>
                        ) : (
                          <span className="text-xs text-slate-400">{t('rsNoCurfew')}</span>
                        )}
                        {row.curfew_window ? (
                          <div className="mt-0.5 text-[10px] text-slate-400">{row.curfew_window}</div>
                        ) : null}
                        {row.curfew_legal_basis ? (
                          <div className="mt-0.5 max-w-[14rem] text-[10px] text-slate-400">
                            {row.curfew_legal_basis}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">
                        {row.customer_ref ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 ? (
          <div className="border-t border-slate-100 px-6 py-3">
            <Pagination
              variant="numbered"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
}
